import { Box } from '@mui/material';
import { CircleAlert, CircleCheck, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import type { ColorTokens } from '@/ui/tokens';
import { Icon } from '@/ui/kit';
import { fontWeights, radius } from '@/ui/tokens';

/**
 * Message de l'ecran de connexion, dans le langage visuel de la variante « Q » :
 * aplat doux, pictogramme et texte plein, rayon 14.
 *
 * Remplace l'`Alert` de MUI de l'ancien ecran. Deux tons seulement : `error` pour
 * ce qui a echoue (persistant, annonce tout de suite — `role="alert"`), `success`
 * pour ce qui a reussi (annonce poliment — `role="status"`). Le sens ne tient
 * jamais a la couleur seule : pictogramme different, et le texte dit ce qui s'est
 * passe.
 */
export type AuthNoticeTone = 'error' | 'success';

const TONES: Readonly<
  Record<AuthNoticeTone, { readonly icon: LucideIcon; readonly ink: keyof ColorTokens; readonly fill: keyof ColorTokens }>
> = {
  error: { icon: CircleAlert, ink: 'criticalText', fill: 'criticalSoft' },
  success: { icon: CircleCheck, ink: 'successText', fill: 'successSoft' },
};

export interface AuthNoticeProps {
  readonly tone: AuthNoticeTone;
  readonly children: ReactNode;
}

export const AuthNotice = ({ tone, children }: AuthNoticeProps): ReactElement => {
  const { icon, ink, fill } = TONES[tone];

  return (
    <Box
      role={tone === 'error' ? 'alert' : 'status'}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        mb: 2.5,
        px: 2,
        py: 1.5,
        borderRadius: `${String(radius.field)}px`,
        bgcolor: `tokens.${fill}`,
        color: `tokens.${ink}`,
        fontSize: 14,
        fontWeight: fontWeights.semiBold,
        lineHeight: '20px',
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', mt: '1px' }}>
        <Icon icon={icon} size={18} />
      </Box>
      <Box component="span" sx={{ minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
};
